import React, { useEffect, useMemo, useState } from 'react';
import { useLazyGetCalendarContactsQuery } from '../../api/calendar.js';
import { useGetParticipantRoleTypesQuery } from '../../api/misc.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import BaseAutocomplete from '../form/BaseAutocomplete.jsx';
import BaseButton from '../form/BaseButton.jsx';
import BaseDropdown from '../form/BaseDropdown.jsx';
import BaseInput from '../form/BaseInput.jsx';
import IconAdd from '../images/IconAdd.jsx';
import BaseCard from './BaseCard.jsx';

const MeetingParticipants = ({
    fields,
    append,
    control,
    clearErrors,
    remove,
    addNew,
    showSearch,
    setShowSearch,
    canAdd
}) => {
    const [search, setSearch] = useState('');
    const [contactEmails, setContactEmails] = useState([]);
    const [newParticipant, setNewParticipant] = useState({
        contact_id: '',
        first_name: '',
        last_name: '',
        email: '',
        role: ''
    });

    const [showParticipantForm, setShowParticipantForm] = useState(false);
    const addNewParticipant = () => {
        setNewParticipant({
            contact_id: '',
            first_name: '',
            last_name: '',
            email: '',
            role: ''
        });
        setShowParticipantForm(true);
        setShowSearch(false);
        setContactList([]);
        setSearch('');
    };

    const { data: participantRoleTypes } = useGetParticipantRoleTypesQuery({});
    const roleOptions = useMemo(() => {
        if (participantRoleTypes?.response?.participant_role_types) {
            return participantRoleTypes.response.participant_role_types;
        }

        return [];
    }, [participantRoleTypes]);

    const [contactList, setContactList] = useState([]);
    const [getCalendarContacts, { isLoading: isLoadingContacts, isError: isErrorContacts }] =
        useLazyGetCalendarContactsQuery();

    const debouncedSearch = useDebounce(search, 500);

    const selectContact = (id) => {
        const contact = contactList.find((item) => item.id === id);
        setNewParticipant({
            contact_id: contact.id,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            email: '',
            role: '',
            avatar: contact.avatar || ''
        });
        setShowParticipantForm(true);
        setContactEmails(contact.emails);
        setContactList([]);
        setSearch('');
        setShowSearch(false);
    };

    const resetSearch = () => {
        setShowSearch(true);
        setContactEmails([]);
        setContactList([]);
        setSearch('');
        setNewParticipant({
            contact_id: '',
            first_name: '',
            last_name: '',
            email: '',
            role: ''
        });
        setShowParticipantForm(false);
    };

    const saveParticipant = () => {
        if ((newParticipant.first_name || newParticipant.last_name) && newParticipant.role) {
            append(newParticipant);
            setNewParticipant({
                contact_id: '',
                first_name: '',
                last_name: '',
                email: '',
                role: ''
            });
            setShowParticipantForm(false);
            setShowSearch(true);
        }
    };

    useEffect(() => {
        (async () => {
            if (debouncedSearch !== '') {
                const result = await getCalendarContacts({ search: debouncedSearch });

                setContactList(result.data.response.contacts);
                setContactEmails([]);
            } else {
                setContactList([]);
            }
        })();
    }, [debouncedSearch, getCalendarContacts]);

    return (
        <BaseCard className='border-0 bg-theme1-primary2 p-0 pb-6 mt-0 max-w-3xl w-full mx-auto'>
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-white'>Participants</div>
                <div onClick={() => resetSearch()}>
                    <IconAdd className='w-8 h-8 fill-theme1-white' />
                </div>
            </div>
            <BaseCard className='border-0 m-0 mb-6 p-4 pb-5 bg-theme1-gray'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='text-lg font-bold text-theme1-title2 pb-2'>Meeting Attendance</div>

                    {showSearch && (
                        <div className='flex justify-between items-center'>
                            <div className='flex-grow'>
                                <BaseAutocomplete
                                    items={contactList.map((contact) => ({
                                        label:
                                            (contact.first_name ?? '') +
                                            (contact.first_name && contact.last_name ? ' ' : '') +
                                            (contact.last_name ?? ''),
                                        value: contact.id
                                    }))}
                                    onSelect={selectContact}
                                    name='search'
                                    value={search}
                                    placeholder='Search...'
                                    setValue={(value) => setSearch(value)}
                                    canAdd={canAdd}
                                    onAddNew={addNewParticipant}
                                    isLoading={isLoadingContacts}
                                    exclude={fields.map((item) => item.contact_id)}
                                    addText='Add new contact'
                                />
                            </div>
                            <div className='text-theme1-primary2 cursor-pointer ml-4' onClick={() => resetSearch()}>
                                Reset
                            </div>
                        </div>
                    )}

                    {showParticipantForm && (
                        <div>
                            {newParticipant.contact_id && (
                                <div className='font-bold text-theme1-primary border border-theme1-border1 py-2 px-4 rounded-lg'>
                                    {newParticipant.first_name + ' ' + newParticipant.last_name}
                                </div>
                            )}
                            {!newParticipant.contact_id && (
                                <>
                                    <BaseInput
                                        type='text'
                                        placeholder='First name'
                                        name='first_name'
                                        value={newParticipant.first_name}
                                        setValue={(value) =>
                                            setNewParticipant({ ...newParticipant, first_name: value })
                                        }
                                    />
                                    <BaseInput
                                        className='mt-4'
                                        type='text'
                                        placeholder='Last name'
                                        name='last_name'
                                        value={newParticipant.last_name}
                                        setValue={(value) => setNewParticipant({ ...newParticipant, last_name: value })}
                                    />
                                </>
                            )}

                            {contactEmails?.length > 0 ? (
                                <BaseDropdown
                                    className='mt-4'
                                    value={newParticipant.email}
                                    name='email'
                                    placeholder='Email for comms'
                                    setValue={(value) => setNewParticipant({ ...newParticipant, email: value })}
                                    options={contactEmails}
                                />
                            ) : (
                                <BaseInput
                                    className='mt-4'
                                    type='text'
                                    placeholder='Email for comms'
                                    name='email'
                                    value={newParticipant.email}
                                    setValue={(value) => setNewParticipant({ ...newParticipant, email: value })}
                                />
                            )}

                            <BaseDropdown
                                className='mt-4'
                                value={newParticipant.role}
                                name='role'
                                placeholder='Choose role'
                                setValue={(value) => setNewParticipant({ ...newParticipant, role: value })}
                                options={roleOptions}
                            />

                            <BaseButton
                                id='btn-add-participant'
                                className='mt-4 btn-red-outline'
                                onClick={() => saveParticipant()}
                                textValue='Save'
                            />
                            <BaseButton
                                id='btn-add-participant'
                                className='mt-4 btn-red'
                                onClick={() => resetSearch()}
                                textValue='Cancel'
                            />
                        </div>
                    )}
                    {fields.length > 0 && (
                        <>
                            <hr className='my-4 border-theme1-disabled' />
                            {fields.map((participant, index) => {
                                return (
                                    <div
                                        key={participant.id}
                                        className='w-full flex items-center justify-between mt-4 bg-theme1-white rounded-lg p-4 border-theme1-cardStroke'
                                    >
                                        <div className='flex flex-row items-center justify-center'>
                                            <div className='mr-4'>
                                                <div className='rounded-full w-12 h-12 overflow-hidden'>
                                                    {participant.avatar ? (
                                                        <img
                                                            src={participant.avatar}
                                                            alt='Avatar'
                                                            className='w-full h-full object-cover'
                                                        />
                                                    ) : (
                                                        <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                            <div className='text-theme1-primary font-bold text-xl'>
                                                                {(participant.first_name + ' ' + participant.last_name)
                                                                    .split(' ')
                                                                    .map((n) => n.substring(0, 1).toUpperCase())
                                                                    .join('')}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className='text-theme1-primary font-bold text-lg'>
                                                    {participant.first_name + ' ' + participant.last_name}
                                                </div>
                                                <div className='text-sm text-theme1-primary2 font-bold'>
                                                    {roleOptions.find((role) => role.value === participant.role)?.label}
                                                </div>
                                            </div>
                                        </div>
                                        <BaseButton
                                            className='ml-2 btn-red max-w-32 p-2'
                                            onClick={() => remove(index)}
                                            textValue='Remove'
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </BaseCard>
        </BaseCard>
    );
};

export default MeetingParticipants;
